/* istanbul ignore file */
import { getUserTrackingId } from '@linearb/linjs-common';
import { SESSION_STORAGE_KEYS } from 'common/constants/constants';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import _ from 'lodash';

/**
 * Analytics base attributes that should be logged with each event
 * @typedef {object} BaseAttributes
 * @property {string} fullStorySessionUrl `FullStory` session recording URL
 */

/**
 * @type {BaseAttributes}
 */
const baseAttributes = {};

export const logFullStorySessionUrl = () => {
  const fullStorySessionUrl = FS?.getCurrentSessionURL(true);

  baseAttributes.fullStorySessionUrl = fullStorySessionUrl;
};

export const updatePage = () => {
  // Special logic to identify datadog synthetic tests - otherwise we pay for those anonymous MTUs...
  if (_.endsWith(window?.navigator.userAgent, 'DatadogSynthetics')) {
    window?.analytics.identify('DATADOG_SYNTH');
  }

  window?.analytics?.page();
  window?.Appcues?.page();
  const _hsq = (window._hsq = window._hsq || []);
  _hsq.push(['trackPageView']);
};

export const logEvent = (eventName, eventAttributes) => {
  const attrs = { ...baseAttributes, ...eventAttributes };

  // Push even to Google Tag Manager
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const isTestFlow = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.TEST_FLOW);
  if (isTestFlow) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...attrs });

  window.analytics && window.analytics.track(eventName, attrs);

  if (!window.amplitude || !window.amplitude.getInstance) {
    // eslint-disable-next-line no-console
    console.log('amplitude is not loaded.', eventName, attrs);
  } else {
    window.amplitude.getInstance().logEvent(eventName, attrs);
  }

  window.Appcues && window.Appcues.track(eventName, attrs);
};

export const setUserId = ({ userId, email }) => {
  window.analytics && window.analytics.identify(userId, { email });

  if (window.TrackJS) {
    window.TrackJS.addMetadata('account_id', userId);
  }

  if (window.DD_LOGS) {
    window.DD_LOGS && window.DD_LOGS.logger.addContext('account_id', userId);
  }

  const _hsq = (window._hsq = window._hsq || []);
  _hsq.push([
    'identify',
    {
      email,
      id: userId
    }
  ]);
  _hsq.push(['trackPageView']);

  if (window.Appcues) {
    window.Appcues.identify(userId, { email });
  }

  if (!window.amplitude || !window.amplitude.getInstance) {
    // eslint-disable-next-line no-console
    console.log('amplitude is not loaded.', userId);
  } else {
    window.amplitude.getInstance().setUserId(userId);
  }
};

export const setUserProperties = ({
  user: { userId, email },
  attrs,
  forGroup = false
}) => {
  if (!forGroup) {
    // skip group updates
    window.analytics && window.analytics.identify(userId, attrs);
  }

  if (window.FS) {
    window.FS.identify(userId, attrs); // identify can be called only once, so we call setUserVars to make sure all props getting updated into FS dict anyway
    window.FS.setUserVars(attrs);
  }
  if (window.TrackJS) {
    _.each(attrs, (v, k) => window.TrackJS.addMetadata(k, v));
  }

  if (window.DD_LOGS) {
    _.each(
      attrs,
      (v, k) => window.DD_LOGS && window.DD_LOGS.logger.addContext(k, v)
    );
  }

  if (window.Appcues) {
    window.Appcues.identify(userId, attrs);
  }

  if (!window.amplitude || !window.amplitude.getInstance) {
    // eslint-disable-next-line no-console
    console.log('amplitude is not loaded.', attrs);
  } else {
    window.amplitude.getInstance().setUserProperties(attrs);
  }
};

export const setGroupProperties = (org_id, org_attrs) => {
  // currently only Segment and appcues accept a separate group attrs call
  window.analytics?.group(org_id, org_attrs);
  window.Appcues?.group(org_id, org_attrs);
};

export const trackOrgProperties = (account, organization) => {
  if (organization) {
    const org_attrs = {
      org: organization.name,
      org_id: organization.id,
      org_license: organization.license.plan_tier_type,
      org_license_expiration: organization.license.end_date,
      plan_name: organization.license.plan_name
    };
    setUserProperties({
      user: getUserTrackingId(account),
      attrs: org_attrs,
      forGroup: true
    });
    setGroupProperties(organization.id, org_attrs);
  }
};
